import { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import type { ComponentType } from 'react';
import {
  Activity,
  BookOpen,
  CheckSquare,
  ChevronRight,
  Contact as ContactIcon,
  Cpu,
  FileCheck2,
  FileSignature,
  FileText,
  FolderKanban,
  Globe,
  Inbox,
  Kanban,
  KeyRound,
  LayoutDashboard,
  MessageSquare,
  MessageSquarePlus,
  Newspaper,
  Phone,
  Receipt,
  Repeat,
  Settings,
  Sparkles,
  Timer,
  type LucideProps,
  Users,
  X,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { BrandMark } from '@/components/brand-mark';
import { useMe } from '@/lib/auth';
import { useOpenApprovablesCount } from '@/lib/approvables';
import { useOpenLeadTasksCount } from '@/lib/lead-tasks';
import { useUnreadMessagesCount } from '@/lib/messages';
import { usePendingReceiptsCount } from '@/lib/subscription-charges';

type Surface = 'admin' | 'portal';

type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  end?: boolean;
  /** Render the matching badge from the live counters. */
  badge?: 'approvals' | 'tasks' | 'messages' | 'receipts';
  /** Portal-only: hide this item unless the user holds this permission. */
  permission?: string;
};

type NavSection = {
  /** Omit for the top section (no header). */
  label?: string;
  items: NavItem[];
  /** Sections marked collapsible default to closed and persist locally. */
  collapsible?: boolean;
  /** Stable storage key for the open/closed state. */
  storageKey?: string;
};

const adminSections: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/messages', label: 'Messages', icon: MessageSquare, badge: 'messages' },
      { to: '/calls', label: 'Calls', icon: Phone },
      { to: '/requests', label: 'Requests', icon: MessageSquarePlus },
      { to: '/time', label: 'Time', icon: Timer },
    ],
  },
  {
    label: 'CRM',
    items: [
      { to: '/leads/pipeline', label: 'Pipeline', icon: Kanban },
      { to: '/leads', label: 'Leads', icon: Sparkles, end: true },
      { to: '/contacts', label: 'Contacts', icon: ContactIcon, end: true },
      { to: '/leads/tasks', label: 'Tasks', icon: CheckSquare, badge: 'tasks' },
    ],
  },
  {
    label: 'Work',
    items: [
      { to: '/clients', label: 'Clients', icon: Users },
      { to: '/projects', label: 'Projects', icon: FolderKanban },
      { to: '/sites', label: 'Websites', icon: Globe },
      { to: '/forms', label: 'Form inbox', icon: Inbox },
      { to: '/blog', label: 'Blog', icon: Newspaper },
      { to: '/approvables', label: 'Approvals', icon: FileCheck2, badge: 'approvals' },
    ],
  },
  {
    label: 'Money',
    items: [
      { to: '/invoices', label: 'Invoices', icon: Receipt },
      { to: '/subscriptions', label: 'Subscriptions', icon: Repeat, badge: 'receipts' },
      { to: '/accounting', label: 'Accounting', icon: BookOpen },
    ],
  },
  {
    label: 'Library',
    collapsible: true,
    storageKey: 'sidebar.library.open',
    items: [
      { to: '/documents', label: 'Documents', icon: FileText },
      { to: '/assets', label: 'Assets', icon: Cpu },
    ],
  },
];

const portalSections: NavSection[] = [
  {
    items: [
      { to: '/', label: 'Dashboard', icon: LayoutDashboard, end: true },
      { to: '/projects', label: 'Projects', icon: FolderKanban, permission: 'projects' },
      { to: '/proposals', label: 'Proposals', icon: FileSignature, permission: 'proposals.view' },
      { to: '/invoices', label: 'Invoices', icon: Receipt, permission: 'invoices.view' },
      { to: '/subscriptions', label: 'Subscriptions', icon: Repeat, permission: 'subscriptions' },
      { to: '/blog', label: 'Blog', icon: Newspaper, permission: 'blog' },
      { to: '/assets', label: 'IT Assets', icon: Cpu, permission: 'assets' },
      { to: '/documents', label: 'Documents', icon: FileText, permission: 'documents' },
      { to: '/vault', label: 'Passwords', icon: KeyRound, permission: 'credentials' },
      { to: '/team', label: 'Team', icon: Users, permission: 'contacts' },
      { to: '/activity', label: 'Activity', icon: Activity, permission: 'activity' },
      { to: '/forms', label: 'Website forms', icon: Inbox, permission: 'form_submissions' },
      { to: '/requests', label: 'Requests', icon: MessageSquarePlus, permission: 'requests' },
    ],
  },
];

type SidebarProps = {
  surface: Surface;
  mobileOpen: boolean;
  onMobileClose: () => void;
};

export function Sidebar({ surface, mobileOpen, onMobileClose }: SidebarProps) {
  const me = useMe();
  const permissions = me.data?.portal_permissions ?? [];
  const sections = (surface === 'admin' ? adminSections : portalSections)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.permission || permissions.includes(item.permission)),
    }))
    .filter((section) => section.items.length > 0);
  const approvalsCount = useOpenApprovablesCount();
  const tasksCount = useOpenLeadTasksCount();
  const messagesCount = useUnreadMessagesCount(surface === 'admin');
  const receiptsCount = usePendingReceiptsCount(surface === 'admin');
  const counts: Record<NonNullable<NavItem['badge']>, number | undefined> = {
    approvals: approvalsCount.data,
    tasks: tasksCount.data,
    messages: messagesCount.data,
    receipts: receiptsCount.data,
  };

  useEffect(() => {
    if (!mobileOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onMobileClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, onMobileClose]);

  return (
    <>
      {/* Backdrop for the mobile drawer. Click to close. */}
      <div
        aria-hidden
        onClick={onMobileClose}
        className={cn(
          'fixed inset-0 z-30 bg-black/40 transition-opacity md:hidden',
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      />

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex h-full w-60 shrink-0 flex-col border-r border-[var(--color-sidebar-border)] bg-[var(--color-sidebar)] text-[var(--color-sidebar-foreground)] transition-transform',
          'md:static md:translate-x-0',
          mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        )}
      >
        <div className="flex h-14 items-center justify-between px-4">
          <span className="flex items-center gap-2.5">
            <BrandMark className="size-7 shrink-0" />
            <span className="flex flex-col leading-tight">
              <span className="text-sm font-semibold tracking-tight">Mehrutherm</span>
              <span className="text-[11px] text-[var(--color-sidebar-muted-foreground)]">
                {surface === 'admin' ? 'Admin' : 'Client portal'}
              </span>
            </span>
          </span>
          <button
            type="button"
            onClick={onMobileClose}
            aria-label="Close navigation"
            className="inline-flex size-7 items-center justify-center rounded-md text-[var(--color-sidebar-muted-foreground)] hover:bg-[var(--color-sidebar-accent)] hover:text-[var(--color-sidebar-foreground)] md:hidden"
          >
            <X className="size-4" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-2">
          {sections.map((section, i) => (
            <SidebarSection
              key={section.label ?? `__top-${i}`}
              section={section}
              counts={counts}
              onNavigate={onMobileClose}
              first={i === 0}
            />
          ))}
        </nav>

        {surface === 'admin' ? (
          <div className="border-t border-[var(--color-sidebar-border)] px-2 py-2">
            <NavLink
              to="/settings"
              onClick={onMobileClose}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-[var(--color-sidebar-muted-foreground)] hover:bg-[var(--color-sidebar-accent)] hover:text-[var(--color-sidebar-foreground)]',
                  isActive && 'bg-[var(--color-sidebar-accent)] text-[var(--color-sidebar-primary)]',
                )
              }
            >
              <Settings className="size-4" />
              Settings
            </NavLink>
          </div>
        ) : null}
      </aside>
    </>
  );
}

function SidebarSection({
  section,
  counts,
  onNavigate,
  first,
}: {
  section: NavSection;
  counts: Record<NonNullable<NavItem['badge']>, number | undefined>;
  onNavigate: () => void;
  first: boolean;
}) {
  const [open, setOpen] = useState<boolean>(() => {
    if (!section.collapsible || !section.storageKey) return true;
    try {
      return localStorage.getItem(section.storageKey) === '1';
    } catch {
      return false;
    }
  });

  function toggle() {
    setOpen((prev) => {
      const next = !prev;
      if (section.storageKey) {
        try {
          localStorage.setItem(section.storageKey, next ? '1' : '0');
        } catch {
          /* ignore */
        }
      }
      return next;
    });
  }

  const collapsed = section.collapsible && !open;
  const labelCls =
    'flex w-full items-center gap-1 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--color-sidebar-muted-foreground)] transition-colors hover:text-[var(--color-sidebar-foreground)]';

  return (
    <div className={first ? '' : 'mt-4'}>
      {section.label ? (
        section.collapsible ? (
          <button type="button" onClick={toggle} className={labelCls} aria-expanded={open}>
            <ChevronRight
              className={cn('size-3 transition-transform', open ? 'rotate-90' : '')}
            />
            <span className="flex-1 text-left">{section.label}</span>
          </button>
        ) : (
          <div className={labelCls}>
            <span className="flex-1 text-left">{section.label}</span>
          </div>
        )
      ) : null}

      {collapsed ? null : (
        <ul className="mt-0.5 space-y-0.5">
          {section.items.map((item) => (
            <li key={item.to}>
              <SidebarLink
                item={item}
                badgeCount={item.badge ? counts[item.badge] : undefined}
                onNavigate={onNavigate}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SidebarLink({
  item,
  badgeCount,
  onNavigate,
}: {
  item: NavItem;
  badgeCount: number | undefined;
  onNavigate: () => void;
}) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm text-[var(--color-sidebar-muted-foreground)] hover:bg-[var(--color-sidebar-accent)] hover:text-[var(--color-sidebar-foreground)]',
          isActive && 'bg-[var(--color-sidebar-accent)] text-[var(--color-sidebar-primary)]',
        )
      }
    >
      <item.icon className="size-4" />
      <span className="flex-1 truncate">{item.label}</span>
      {badgeCount && badgeCount > 0 ? (
        <span className="inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-sidebar-primary)]/20 px-1.5 text-[10px] font-medium tabular-nums text-[var(--color-sidebar-primary)]">
          {badgeCount > 99 ? '99+' : badgeCount}
        </span>
      ) : null}
    </NavLink>
  );
}
